import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import type { Availability, AvailabilityDay } from './api';
import { compareTimes, normalizeTime } from './dates';

/**
 * Editing state of the availability week (Beratung 08, Einstellungen 14, EM6): up to two windows per day,
 * validation with the server's sentences, server field errors mapped back to the rows.
 */

export interface WindowState {
  start: string;
  end: string;
}

export interface DayState {
  weekday: number;
  label: string;
  enabled: boolean;
  windows: WindowState[];
}

export type DayErrors = Record<number, string>;

export const MAX_WINDOWS = 2;
export const DEFAULT_WINDOW: WindowState = { start: '09:00', end: '17:00' };

export const MESSAGES = {
  endBeforeStart: 'Das Ende muss nach dem Beginn liegen.',
  overlap: 'Die Zeitfenster überschneiden sich.',
  noWindow: 'Bitte mindestens ein Zeitfenster angeben.',
  invalidTime: 'Bitte eine Uhrzeit im Format HH:MM eingeben.',
} as const;

export function daysFrom(data: Availability): DayState[] {
  return data.days.map((day) => ({
    weekday: day.weekday,
    label: day.label,
    enabled: day.enabled,
    windows: day.windows.map((window) => ({ start: window.start, end: window.end })),
  }));
}

/** Client-side validation with the server's sentences; returns one message per weekday. */
export function validateDays(days: readonly DayState[]): DayErrors {
  const errors: DayErrors = {};
  for (const day of days) {
    if (!day.enabled) continue;
    const windows = day.windows.filter((window) => window.start !== '' || window.end !== '');
    if (windows.length === 0) {
      errors[day.weekday] = MESSAGES.noWindow;
      continue;
    }
    for (const window of windows) {
      if (normalizeTime(window.start) === null || normalizeTime(window.end) === null) {
        errors[day.weekday] = MESSAGES.invalidTime;
        break;
      }
      if (compareTimes(window.end, window.start) <= 0) {
        errors[day.weekday] = MESSAGES.endBeforeStart;
        break;
      }
    }
    if (errors[day.weekday] !== undefined) continue;
    const sorted = [...windows].sort((a, b) => compareTimes(a.start, b.start));
    for (let index = 1; index < sorted.length; index += 1) {
      const previous = sorted[index - 1];
      const current = sorted[index];
      if (previous && current && compareTimes(current.start, previous.end) < 0) {
        errors[day.weekday] = MESSAGES.overlap;
        break;
      }
    }
  }
  return errors;
}

/** The PUT body: every weekday, windows sorted, empty second windows dropped. */
export function toBody(
  days: readonly DayState[],
): Array<Pick<AvailabilityDay, 'weekday' | 'enabled' | 'windows'>> {
  return days.map((day) => ({
    weekday: day.weekday,
    enabled: day.enabled,
    windows: day.enabled
      ? day.windows
          .filter((window) => window.start !== '' && window.end !== '')
          .map((window) => ({ start: window.start, end: window.end }))
          .sort((a, b) => compareTimes(a.start, b.start))
      : [],
  }));
}

/** `days.2.windows.1.end` → the weekday of row 2 of the sent body. */
export function serverErrorsToDays(error: unknown, sent: readonly DayState[]): DayErrors {
  if (!(error instanceof ApiError)) return {};
  const errors: DayErrors = {};
  for (const [field, message] of Object.entries(error.fields)) {
    const match = /^days\.(\d+)/.exec(field);
    if (!match) continue;
    const weekday = sent[Number(match[1])]?.weekday;
    if (weekday !== undefined && errors[weekday] === undefined) errors[weekday] = message;
  }
  return errors;
}

export function sameDays(a: readonly DayState[], b: readonly DayState[]): boolean {
  return JSON.stringify(toBody(a)) === JSON.stringify(toBody(b));
}

export interface AvailabilityForm {
  days: DayState[];
  errors: DayErrors;
  dirty: boolean;
  setEnabled: (weekday: number, enabled: boolean) => void;
  setWindow: (weekday: number, index: number, field: keyof WindowState, value: string) => void;
  addWindow: (weekday: number) => void;
  removeWindow: (weekday: number, index: number) => void;
  replaceDay: (day: DayState) => void;
  /** Validates and returns the body, or null (errors are set). */
  prepare: () => ReturnType<typeof toBody> | null;
  setServerError: (error: unknown) => void;
  reset: () => void;
}

/** Local editing state of the week, initialised from the loaded availability (re-initialised when it changes). */
export function useAvailabilityForm(data: Availability | undefined): AvailabilityForm {
  const initial = useMemo(() => (data ? daysFrom(data) : []), [data]);
  const [days, setDays] = useState<DayState[]>(initial);
  const [errors, setErrors] = useState<DayErrors>({});
  const lastInitial = useRef(initial);

  useEffect(() => {
    if (lastInitial.current !== initial) {
      lastInitial.current = initial;
      setDays(initial);
      setErrors({});
    }
  }, [initial]);

  const update = useCallback((weekday: number, change: (day: DayState) => DayState) => {
    setDays((current) => current.map((day) => (day.weekday === weekday ? change(day) : day)));
    setErrors((current) => {
      if (current[weekday] === undefined) return current;
      const next = { ...current };
      delete next[weekday];
      return next;
    });
  }, []);

  const setEnabled = useCallback(
    (weekday: number, enabled: boolean) =>
      update(weekday, (day) => ({
        ...day,
        enabled,
        windows: enabled && day.windows.length === 0 ? [{ ...DEFAULT_WINDOW }] : day.windows,
      })),
    [update],
  );

  const setWindow = useCallback(
    (weekday: number, index: number, field: keyof WindowState, value: string) =>
      update(weekday, (day) => ({
        ...day,
        windows: day.windows.map((window, position) =>
          position === index ? { ...window, [field]: value } : window,
        ),
      })),
    [update],
  );

  const addWindow = useCallback(
    (weekday: number) =>
      update(weekday, (day) => {
        if (day.windows.length >= MAX_WINDOWS) return day;
        const last = day.windows[day.windows.length - 1];
        const start = last ? nextHour(last.end) : DEFAULT_WINDOW.start;
        return { ...day, windows: [...day.windows, { start, end: nextHour(start, 4) }] };
      }),
    [update],
  );

  const removeWindow = useCallback(
    (weekday: number, index: number) =>
      update(weekday, (day) => ({
        ...day,
        windows: day.windows.filter((_, position) => position !== index),
      })),
    [update],
  );

  const replaceDay = useCallback((next: DayState) => update(next.weekday, () => next), [update]);

  const prepare = useCallback(() => {
    const found = validateDays(days);
    setErrors(found);
    return Object.keys(found).length === 0 ? toBody(days) : null;
  }, [days]);

  const setServerError = useCallback(
    (error: unknown) => {
      setErrors(serverErrorsToDays(error, days));
    },
    [days],
  );

  const reset = useCallback(() => {
    setDays(initial);
    setErrors({});
  }, [initial]);

  return {
    days,
    errors,
    dirty: !sameDays(days, initial),
    setEnabled,
    setWindow,
    addWindow,
    removeWindow,
    replaceDay,
    prepare,
    setServerError,
    reset,
  };
}

/** "13:00" → "14:00" (capped at 23:30). */
function nextHour(time: string, hours = 1): string {
  const normalized = normalizeTime(time) ?? '09:00';
  const [h = 9, m = 0] = normalized.split(':').map(Number);
  const total = Math.min(h * 60 + m + hours * 60, 23 * 60 + 30);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** „09:00 – 18:00" or „09:00 – 12:30 · 14:00 – 19:00" (EM6). */
export function windowsText(day: DayState): string {
  return day.windows
    .filter((window) => window.start && window.end)
    .map((window) => `${window.start} – ${window.end}`)
    .join(' · ');
}
